import ContactAPI from '../../api/contacts';
import { getChannelColor } from '../../components-next/icon/provider';

const MERGED_TIMELINE_PAGE_SIZE = 50;

const CHANNEL_ICON_MAP = {
  'Channel::Whatsapp': 'i-ri-whatsapp-line',
  'Channel::Email': 'i-ri-mail-line',
  'Channel::FacebookPage': 'i-ri-messenger-line',
  'Channel::Instagram': 'i-ri-instagram-line',
  'Channel::Telegram': 'i-ri-telegram-line',
  'Channel::WebWidget': 'i-ri-global-line',
  'Channel::TwilioSms': 'i-ri-phone-line',
  'Channel::Sms': 'i-ri-phone-line',
  'Channel::Line': 'i-ri-line-line',
  'Channel::TwitterProfile': 'i-ri-twitter-x-line',
  'Channel::Api': 'i-ri-cloudy-line',
  'Channel::Tiktok': 'i-ri-tiktok-line',
  'Channel::Voice': 'i-ri-phone-line',
  'Channel::DinastiaWhatsapp': 'i-ri-whatsapp-line',
};

export { CHANNEL_ICON_MAP };

const state = {
  activeContactId: null,
  contactChannels: [],
  // Whether the "Todas as conversas" merged timeline is active
  isAllTabActive: false,
  // Pagination for the merged timeline
  mergedTimelineLimit: MERGED_TIMELINE_PAGE_SIZE,
  uiFlags: {
    isFetching: false,
  },
};

const getters = {
  getUIFlags: $state => $state.uiFlags,

  getContactChannels: $state => $state.contactChannels,

  isUnifiedViewAvailable: $state => $state.contactChannels.length > 0,

  isAllTabActive: $state => $state.isAllTabActive,

  // Just returns the selected chat (no override)
  getEffectiveChat: (_$state, _getters, _rootState, rootGetters) => {
    return rootGetters.getSelectedChat;
  },

  getLastEmailInEffectiveChat: (_$state, _getters) => {
    const chat = _getters.getEffectiveChat;
    const { messages = [] } = chat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;
      return [0, 1].includes(messageType);
    });
    return lastEmail;
  },

  // Collects and sorts ALL messages from all channels
  allSortedMessagesInternal: ($state, _getters, _rootState, rootGetters) => {
    if ($state.contactChannels.length === 0)
      return { messages: [], channelMap: {} };

    const allMessages = [];
    const channelMap = {};

    $state.contactChannels.forEach(channel => {
      const ids = channel.conversationIds || [channel.conversationId];
      ids.forEach(cId => {
        channelMap[cId] = channel;
        const conv = rootGetters.getConversationById(cId);
        if (conv && conv.messages) {
          conv.messages.forEach(msg => {
            allMessages.push(msg);
          });
        }
      });
    });

    allMessages.sort((a, b) => a.created_at - b.created_at);
    return { messages: allMessages, channelMap };
  },

  hasMoreMergedMessages: ($state, _getters) => {
    return (
      _getters.allSortedMessagesInternal.messages.length >
      $state.mergedTimelineLimit
    );
  },

  // Merged timeline: paginated with channel separator markers
  getMergedTimeline: ($state, _getters) => {
    const { messages: allMessages, channelMap } =
      _getters.allSortedMessagesInternal;
    if (allMessages.length === 0) return [];

    const startIndex = Math.max(
      0,
      allMessages.length - $state.mergedTimelineLimit
    );
    const paginatedMessages = allMessages.slice(startIndex);

    const result = [];
    let lastConvId = null;

    paginatedMessages.forEach(msg => {
      if (msg.conversation_id !== lastConvId) {
        const channel = channelMap[msg.conversation_id];
        if (channel) {
          result.push({
            is_separator: true,
            id: `sep-${msg.conversation_id}-${msg.id}`,
            channel_type: channel.channelType,
            inbox_name: channel.inboxName,
            icon: channel.icon,
            created_at: msg.created_at,
            can_reply: channel.canReply,
          });
        }
        lastConvId = msg.conversation_id;
      }
      result.push(msg);
    });

    return result;
  },
};

const actions = {
  async initForContact({ commit, rootGetters }, contactId) {
    if (!contactId) return;

    commit('SET_UI_FLAGS', { isFetching: true });
    commit('SET_ACTIVE_CONTACT', contactId);
    commit('SET_ALL_TAB_ACTIVE', false);

    try {
      const response = await ContactAPI.getConversations(contactId);
      const conversations = response.data.payload || [];

      const inboxMap = {};
      conversations.forEach(conv => {
        const inbox = rootGetters['inboxes/getInbox'](conv.inbox_id) || {};
        const activityTime = conv.last_activity_at || conv.timestamp;

        if (!inboxMap[conv.inbox_id]) {
          inboxMap[conv.inbox_id] = {
            conversationIds: [conv.id],
            conversationId: conv.id,
            inboxId: conv.inbox_id,
            channelType: inbox.channel_type || '',
            inboxName: inbox.name || `Inbox #${conv.inbox_id}`,
            icon: CHANNEL_ICON_MAP[inbox.channel_type] || 'i-ri-chat-1-line',
            colorClass: getChannelColor(inbox.channel_type, inbox.provider),
            provider: inbox.provider || '',
            lastActivityAt: activityTime,
            status: conv.status,
            canReply: conv.can_reply !== false,
          };
        } else {
          inboxMap[conv.inbox_id].conversationIds.push(conv.id);
          if (
            new Date(activityTime) >
            new Date(inboxMap[conv.inbox_id].lastActivityAt)
          ) {
            inboxMap[conv.inbox_id].conversationId = conv.id;
            inboxMap[conv.inbox_id].lastActivityAt = activityTime;
            inboxMap[conv.inbox_id].status = conv.status;
          }
          if (conv.can_reply !== false) {
            inboxMap[conv.inbox_id].canReply = true;
          }
        }
      });

      const channels = Object.values(inboxMap);
      channels.sort(
        (a, b) => new Date(b.lastActivityAt) - new Date(a.lastActivityAt)
      );

      commit('SET_CONTACT_CHANNELS', channels);
    } catch (error) {
      commit('SET_CONTACT_CHANNELS', []);
    } finally {
      commit('SET_UI_FLAGS', { isFetching: false });
    }
  },

  async switchToAllTab({ commit, state: $state, dispatch, rootGetters }) {
    commit('SET_ALL_TAB_ACTIVE', true);
    commit('SET_MERGED_TIMELINE_LIMIT', MERGED_TIMELINE_PAGE_SIZE);

    const promises = [];
    $state.contactChannels.forEach(channel => {
      const ids = channel.conversationIds || [channel.conversationId];
      ids.forEach(cId => {
        const conv = rootGetters.getConversationById(cId);
        if (!conv) {
          promises.push(dispatch('getConversation', cId, { root: true }));
        }
      });
    });

    await Promise.all(promises);
  },

  exitAllTab({ commit }) {
    commit('SET_ALL_TAB_ACTIVE', false);
  },

  loadMoreMergedMessages({ commit, state: $state }) {
    commit(
      'SET_MERGED_TIMELINE_LIMIT',
      $state.mergedTimelineLimit + MERGED_TIMELINE_PAGE_SIZE
    );
  },

  clear({ commit }) {
    commit('CLEAR');
  },
};

const mutations = {
  SET_UI_FLAGS($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_ACTIVE_CONTACT($state, contactId) {
    $state.activeContactId = contactId;
  },
  SET_CONTACT_CHANNELS($state, channels) {
    $state.contactChannels = channels;
  },
  SET_ALL_TAB_ACTIVE($state, val) {
    $state.isAllTabActive = val;
  },
  SET_MERGED_TIMELINE_LIMIT($state, limit) {
    $state.mergedTimelineLimit = limit;
  },
  CLEAR($state) {
    $state.activeContactId = null;
    $state.contactChannels = [];
    $state.isAllTabActive = false;
    $state.mergedTimelineLimit = MERGED_TIMELINE_PAGE_SIZE;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
